import { useState } from 'react';
import { Heading } from '@/components/ui/heading';
import type { SeatingAreaDto } from '@/services/platform/api/model';
import type { RoomsSliderItemBlock } from './types';
import { resolveHeadingLevel } from '../ui/heading-level';
import { resolveMediaUrl } from '../ui/resolveMediaUrl';
import { MediaSlider } from '../ui/media-slider';
import { MediaSliderCardBody } from '../ui/media-slider-card';
import { RoomDetailSheet } from './room-detail-sheet';

/** A live SevenRooms seating area paired with its matching CMS content. */
export interface RoomCarouselItem {
  area: SeatingAreaDto;
  item: RoomsSliderItemBlock;
}

interface RoomsCarouselBlockProps {
  title?: string | null;
  titleHeading?: string | null;
  rooms: RoomCarouselItem[];
}

export function RoomsCarouselBlock({ title, titleHeading, rooms }: RoomsCarouselBlockProps) {
  const headingAs = resolveHeadingLevel(titleHeading);
  const [selectedRoom, setSelectedRoom] = useState<RoomCarouselItem | null>(null);

  if (!rooms.length) return null;

  return (
    <div className="flex flex-col gap-6 pb-2 pl-4">
      {title && (
        <Heading
          as={headingAs}
          className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[24px] leading-normal font-semibold"
        >
          {title}
        </Heading>
      )}

      <MediaSlider
        items={rooms}
        gapClassName="gap-2"
        gapPx={8}
        fallbackRatio={0.62}
        renderItem={(room, i, cardProps) => (
          <button
            key={i}
            type="button"
            onClick={() => setSelectedRoom(room)}
            className="flex w-[62vw] shrink-0 snap-start flex-col gap-2 text-left"
            {...cardProps}
          >
            {/* Card thumbnail uses the single cover `image`; the full `images` gallery is
                reserved for the detail sheet opened on tap. */}
            <MediaSliderCardBody
              imageUrl={resolveMediaUrl(room.item.image?.url)}
              imageAlt={room.item.title ?? ''}
              title={room.item.title}
              subtitle={room.item.shortDescription}
            />
          </button>
        )}
      />

      <RoomDetailSheet
        room={selectedRoom}
        open={!!selectedRoom}
        onOpenChange={open => !open && setSelectedRoom(null)}
      />
    </div>
  );
}
